/**
 * useFirmUsers — org-scoped firm user management (Phase 4/5).
 * Wraps /v1/rbac/users (firm.manage).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { config } from '../config';

export interface FirmUser {
  userId: string;
  username: string;
  email?: string | null;
  name?: string | null;
  isActive: boolean;
}

export const firmUserKeys = { all: ['rbac', 'users'] as const };

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function useFirmUsers() {
  return useQuery<FirmUser[], Error>({
    queryKey: firmUserKeys.all,
    queryFn: async () => {
      const res = await fetch(`${config.API_URL}/v1/rbac/users`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      return res.json();
    },
  });
}

export interface CreateFirmUserInput {
  email: string;
  name?: string;
  // Optional: the backend derives username from email when omitted.
  username?: string;
  password?: string;
}

export function useCreateFirmUser() {
  const qc = useQueryClient();
  return useMutation<FirmUser, Error, CreateFirmUserInput>({
    mutationFn: async (body) => {
      const res = await fetch(`${config.API_URL}/v1/rbac/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          typeof d?.detail === 'string' ? d.detail : `Failed to create user (${res.status})`,
        );
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: firmUserKeys.all }),
  });
}

export interface InviteLink {
  link: string;
  emailSent: boolean;
  emailTo: string | null;
}

/**
 * useGenerateInviteLink — issues a set-password / login link for a firm user,
 * emails it where the environment allows, and returns it as the copy fallback.
 * Wraps POST /v1/rbac/users/{id}/magic-link (firm.manage, org-scoped).
 */
export function useGenerateInviteLink() {
  return useMutation<InviteLink, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/users/${userId}/magic-link`,
        { method: 'POST', headers: authHeaders() },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          typeof d?.detail === 'string' ? d.detail : `Failed to generate link (${res.status})`,
        );
      }
      const data = await res.json();
      // A response without the email fields reads as not sent.
      return { link: data.magicLink, emailSent: !!data.emailSent, emailTo: data.emailTo ?? null };
    },
  });
}

export function useSetFirmUserActive() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string; isActive: boolean }>({
    mutationFn: async ({ userId, isActive }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/users/${userId}/active`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ isActive }) },
      );
      if (!res.ok && res.status !== 204) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          typeof d?.detail === 'string' ? d.detail : `Failed to update user (${res.status})`,
        );
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: firmUserKeys.all }),
  });
}
